// Suffixes of the `contact.contact.import_form.content.file.*` translation keys, in template order.
export const CONTACT_IMPORT_COLUMN_ROWS: string[][] = [
    ['first_name', 'last_name', 'company_name', 'honorific_prefix', 'email', 'phone', 'addresse', 'addresse_line_2', 'postcode', 'city', 'country'],
    ['billing_addresse', 'billing_addresse_line_2', 'billing_postcode', 'billing_city', 'billing_country', 'vat_number'],
];

export type ContactImportErrorKind = 'invalid_file_type' | 'invalid_file' | 'invalid_content' | 'request_failed';

export interface ContactImportErrorDetail {
    line: number | null;
    message: string;
}

export interface ContactImportErrors {
    kind: ContactImportErrorKind;
    details: ContactImportErrorDetail[];
}

export const isCsvFile = (file: File): boolean => file.name.toLowerCase().endsWith('.csv');

// Row errors are keyed `rows.<index>.<column>`. Index 0 is the first data line, which is line 2 of the CSV.
const lineOf = (field: string): number | null => {
    const match = field.match(/^[^.]+\.(\d+)(\.|$)/);
    return match ? Number(match[1]) + 2 : null;
};

// Same field name as `BaseEndpoint.getJsonApiValidationErrorField`. An error without a pointer gets an empty field.
const fieldOf = (pointer: unknown): string => (typeof pointer === 'string' ? pointer.replace('/data/attributes/', '') : '');

// Pass the raw `errors`: a JSON:API list (`[{ detail, source: { pointer } }]`) or an object keyed by field (`{ field: messages[] }`).
// Not `validationErrors`: the endpoint drops the errors that have no pointer from it.
const toFieldMessages = (error: unknown): [string, string][] => {
    if (Array.isArray(error)) {
        return error.flatMap((item: { detail?: unknown; source?: { pointer?: unknown } }) => (typeof item?.detail === 'string' ? [[fieldOf(item.source?.pointer), item.detail]] : []));
    }
    if (error && typeof error === 'object') {
        return Object.entries(error).flatMap(([field, messages]) => (Array.isArray(messages) && typeof messages[0] === 'string' ? [[field, messages[0]]] : []));
    }
    return [];
};

export const parseContactImportErrors = (error: unknown): ContactImportErrors => {
    const fieldMessages = toFieldMessages(error);
    const details = fieldMessages.map(([field, message]) => ({ line: lineOf(field), message }));

    // The validate button needs a file, so an error on `file` means the API rejected the file itself (size, encoding...).
    if (fieldMessages.some(([field]) => field === 'file')) return { kind: 'invalid_file', details };
    return { kind: fieldMessages.some(([field]) => field !== '') ? 'invalid_content' : 'request_failed', details };
};
